import React, { useEffect, useState, useCallback } from "react";
import { useCampanhas } from "./hooks/useCampanhas";
import {
  Table,
  Button,
  Skeleton,
  Typography,
  Row,
  Col,
  Flex,
  Dropdown,
  Space,
  message,
  Alert,
} from "antd";
import type { TableProps } from "antd/es/table";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "react-router-dom";
import { Container } from "../ViewGerenciarPostagens/styles";
import { useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import { modeloCampanhasService } from "../../services/modeloCampanhasService";

const { Title } = Typography;

export default function ViewGerenciarCampanhas() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [messageApi, contextHolder] = message.useMessage();

  // 🔹 Pega o modeloId da URL
  const params = new URLSearchParams(window.location.search);
  const modeloId = Number(params.get("modeloId")) || 0;

  // 🔹 Nome do modelo
  const [nomeModelo, setNomeModelo] = useState<string>("");

  // 🔹 Hook customizado que busca campanhas
  const { campanhas, loading, erro, fetchCampanhas } = useCampanhas(modeloId);

  useEffect(() => {
    if (modeloId) {
      fetchCampanhas();
      carregarNomeModelo();
    }
  }, [modeloId]);

  const carregarNomeModelo = async () => {
    try {
      const modelo = await modeloCampanhasService.buscarModeloCampanhaPorIdService(modeloId);
      if (modelo?.nome) setNomeModelo(modelo.nome);
    } catch (err) {
      console.error("Erro ao carregar nome do modelo:", err);
    }
  };

  // 🔹 Helpers de mensagens
  const sucesso = (texto: string) => messageApi.open({ type: "success", content: texto });
  const error = (texto: string) => messageApi.open({ type: "error", content: texto });

  // 🔹 Navegação para a tela de mensagens da campanha
  const handleVerMensagens = useCallback(
    (campanhaId?: number) => {
      if (campanhaId) {
        navigate(`/gerenciarMensagens?campanhaId=${campanhaId}`);
      } else {
        error("ID da campanha não encontrado.");
      }
    },
    [navigate]
  );

  // 🔹 Colunas da tabela
  const columns: TableProps<any>["columns"] = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      align: "center",
    },
    {
      title: "Data Início",
      dataIndex: "dataInicio",
      key: "dataInicio",
      align: "center",
      render: (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "—"),
    },
    {
      title: "Data Fim",
      dataIndex: "dataFim",
      key: "dataFim",
      align: "center",
      render: (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "—"),
    },
    {
      title: "Data Criação",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render: (value) => (value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—"),
    },
    {
      title: "Ações",
      key: "acoes",
      align: "center",
      render: (_, row) => {
        const items = [
          {
            key: "1",
            label: "Mensagens",
            onClick: () => handleVerMensagens(row.id),
          },
        ];

        return (
          <Space size="middle">
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button type="text" icon={<SettingsIcon />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <Container>
      {contextHolder}
      {loading ? (
        <Skeleton active />
      ) : (
        <>
          {/* 🔹 Cabeçalho */}
          <Row>
            <Col span={5}>
              <Flex align="center" justify="center" style={{ marginTop: 15 }}>
                <a href={`/gerenciarModeloCampanhas`}>
                  <ArrowBackIcon color="primary" />
                </a>
              </Flex>
            </Col>
            <Col span={12}>
              <Flex align="center" justify="center">
                <HomeIcon color="primary" fontSize="large" />
                <Title
                  style={{
                    marginLeft: 10,
                    marginTop: 10,
                    fontSize: isMobile ? "1rem" : "2rem",
                    color: "rgb(0, 54, 68)",
                  }}
                  level={3}
                >
                  Campanhas — {nomeModelo || "Carregando..."}
                </Title>
              </Flex>
            </Col>
          </Row>

          {/* 🔹 Tabela */}
          {erro ? (
            <Alert message="Erro ao carregar campanhas" description={erro} type="error" showIcon />
          ) : (
            <Row>
              <Col span={24}>
                <Flex
                  justify="center"
                  style={{ marginTop: 20, overflowX: "auto", width: "100%" }}
                >
                  <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={campanhas}
                    bordered
                    pagination={{ pageSize: 10 }}
                    style={{ width: "100%", minWidth: 800 }}
                  />
                </Flex>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
}
